import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Badge,
  Alert,
  AlertDescription,
} from '@startupquickstart/frontend';
import { CheckCircle, AlertCircle, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';

// Special value to represent "no mapping" since Radix Select doesn't allow empty strings
const NO_MAPPING_VALUE = '__NONE__';

export interface ColumnMappingInfo {
  csvHeaders: string[];
  systemColumns: string[];
  requiredColumns: string[];
  currentMappings: Record<string, string | null>;
  defaultMappings: Record<string, string | null>;
  suggestions: Record<string, string[]>;
}

interface ColumnMapperProps {
  info: ColumnMappingInfo;
  mappings: Record<string, string | null>;
  onMappingChange: (systemCol: string, csvCol: string | null) => void;
}

/**
 * Column Mapper Component
 * Allows users to map CSV columns to system columns before validation
 */
export function ColumnMapper({
  info,
  mappings,
  onMappingChange,
}: ColumnMapperProps) {
  const { csvHeaders, systemColumns, requiredColumns, suggestions } = info;

  // Get mapped CSV columns to prevent double-mapping
  const mappedCsvCols = new Set(
    Object.values(mappings).filter((v): v is string => v !== null),
  );

  // Get unmapped CSV columns (for warning display, excluding empty headers)
  const unmappedCsvCols = csvHeaders.filter((h) => h && !mappedCsvCols.has(h));

  // Check which required columns are missing mappings
  const missingRequired = requiredColumns.filter(
    (col) => !mappings[col],
  );

  // Get status for a column mapping
  const getColumnStatus = (systemCol: string): 'mapped' | 'unmapped' | 'suggested' => {
    if (mappings[systemCol]) {
      return 'mapped';
    }
    // Check if any unmapped CSV column has a suggestion for this system column
    const hasSuggestion = Object.entries(suggestions).some(
      ([_csvCol, suggestedSystemCols]) =>
        suggestedSystemCols.includes(systemCol) && !mappedCsvCols.has(_csvCol),
    );
    if (hasSuggestion) {
      return 'suggested';
    }
    return 'unmapped';
  };

  // Get available CSV columns for a system column (including suggestions)
  const getAvailableOptions = (systemCol: string): { value: string; label: string; suggested?: boolean }[] => {
    const options: { value: string; label: string; suggested?: boolean }[] = [];

    // Add currently mapped column first if exists
    const currentMapping = mappings[systemCol];
    if (currentMapping) {
      options.push({ value: currentMapping, label: currentMapping });
    }

    // Add suggested columns for this system column (skip empty headers)
    for (const [csvCol, suggestedSystemCols] of Object.entries(suggestions)) {
      if (csvCol && suggestedSystemCols.includes(systemCol) && !mappedCsvCols.has(csvCol) && csvCol !== currentMapping) {
        options.push({ value: csvCol, label: `${csvCol} (suggested)`, suggested: true });
      }
    }

    // Add other unmapped columns (skip empty headers)
    for (const csvCol of csvHeaders) {
      if (csvCol && !mappedCsvCols.has(csvCol) && csvCol !== currentMapping) {
        const isSuggested = suggestions[csvCol]?.includes(systemCol);
        if (!isSuggested) {
          options.push({ value: csvCol, label: csvCol });
        }
      }
    }

    return options;
  };

  return (
    <div className="space-y-6">
      {/* Missing Required Warning */}
      {missingRequired.length > 0 && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <strong>{missingRequired.length} required {missingRequired.length === 1 ? 'column is' : 'columns are'} unmapped:</strong>{' '}
            {missingRequired.join(', ')}
          </AlertDescription>
        </Alert>
      )}

      {/* Unmapped CSV Columns Warning */}
      {unmappedCsvCols.length > 0 && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <strong>{unmappedCsvCols.length} CSV {unmappedCsvCols.length === 1 ? 'column' : 'columns'} won't be imported:</strong>{' '}
            {unmappedCsvCols.slice(0, 5).join(', ')}
            {unmappedCsvCols.length > 5 && ` and ${unmappedCsvCols.length - 5} more`}
          </AlertDescription>
        </Alert>
      )}

      {/* Column Mapping Table */}
      <div className="rounded-xl border overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-muted/50 border-b">
              <th className="text-left px-4 py-3 text-sm font-medium">System Column</th>
              <th className="text-center px-4 py-3 text-sm font-medium w-24">Required</th>
              <th className="text-left px-4 py-3 text-sm font-medium">Your CSV Column</th>
              <th className="text-center px-4 py-3 text-sm font-medium w-20">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {systemColumns.map((systemCol) => {
              const isRequired = requiredColumns.includes(systemCol);
              const status = getColumnStatus(systemCol);
              const options = getAvailableOptions(systemCol);
              const currentValue = mappings[systemCol] || '';

              return (
                <tr key={systemCol} className={cn(
                  'transition-colors',
                  isRequired && !mappings[systemCol] && 'bg-destructive/5',
                )}>
                  <td className="px-4 py-3">
                    <span className="font-medium">{systemCol}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {isRequired && (
                      <Badge variant="secondary" className="text-xs">
                        Required
                      </Badge>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <Select
                      value={currentValue || NO_MAPPING_VALUE}
                      onValueChange={(value) => {
                        onMappingChange(systemCol, value === NO_MAPPING_VALUE ? null : value);
                      }}
                    >
                      <SelectTrigger className="w-full max-w-xs">
                        <SelectValue placeholder="Select column..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NO_MAPPING_VALUE}>
                          <span className="text-muted-foreground">None (skip)</span>
                        </SelectItem>
                        {options.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            <span className={cn(opt.suggested && 'text-primary font-medium')}>
                              {opt.label}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {status === 'mapped' && (
                      <CheckCircle className="h-5 w-5 text-emerald-500 mx-auto" />
                    )}
                    {status === 'suggested' && (
                      <AlertTriangle className="h-5 w-5 text-amber-500 mx-auto" />
                    )}
                    {status === 'unmapped' && isRequired && (
                      <AlertCircle className="h-5 w-5 text-destructive mx-auto" />
                    )}
                    {status === 'unmapped' && !isRequired && (
                      <span className="text-muted-foreground text-sm">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Help text */}
      <p className="text-sm text-muted-foreground">
        Map your CSV columns to the system fields. Required fields must be mapped before proceeding.
        Unmapped CSV columns will be ignored during import.
      </p>
    </div>
  );
}
